import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowRight } from 'lucide-react';
import SEOMetadata from '@/components/SEOMetadata';
import Breadcrumb from '@/components/landing/common/Breadcrumb';
import { breakpoints } from '@/layouts/breakpoints';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';
import { ThinkingOrb } from 'thinking-orbs';

const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const comparisonModules = import.meta.glob('/src/pages/compare/*.tsx', { eager: true });
const EXCLUDED = new Set(['/src/pages/compare/ComparisonPage.tsx']);

interface ComparisonEntry {
  slug: string;
  title: string;
  description: string;
}

const entries: ComparisonEntry[] = Object.keys(comparisonModules)
  .filter(path => !EXCLUDED.has(path))
  .map(path => {
    const slug = path.replace('/src/pages/compare/', '').replace('.tsx', '');
    const mod = comparisonModules[path] as any;
    return {
      slug,
      title: mod.meta?.title ?? slug,
      description: mod.meta?.description ?? '',
    };
  })
  .sort((a, b) => a.title.localeCompare(b.title));

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fff;
  padding-top: 120px;
  position: relative;
  @media (max-width: ${breakpoints.medium}) {
    padding-top: 40px;
  }

  .orb{
    position: absolute;
    top: 180px;
    right: 48px;
    transform: scale(2);
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 48px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 32px 16px 64px;
  }
`;

const BreadcrumbWrap = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 38px;
  color: #111;
  margin-bottom: 12px;
  letter-spacing: -0.02em;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 26px;
  }
`;

const Subtitle = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: #555;
  margin-bottom: 48px;
`;

const Divider = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin: 0 0 40px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const ListItem = styled(Link)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 32px;
  padding: 20px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.07);
  text-decoration: none;
  color: inherit;
  transition: opacity 0.15s;

  &:last-child {
    border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }

  &:hover {
    opacity: 0.55;
  }

  @media (max-width: ${breakpoints.medium}) {
    flex-direction: column;
    gap: 6px;
  }
`;

const ListLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const ListTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin: 0 0 4px;
  line-height: 1.4;
`;

const ListDesc = styled.p`
  font-size: 14px;
  color: #555;
  line-height: 1.55;
  margin: 0;
  max-width: 600px;
`;

const ListArrow = styled(ArrowRight)`
  flex-shrink: 0;
  color: #999;
  transition: color 0.15s;

  ${ListItem}:hover & {
    color: #111;
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function CompareIndexPage() {
  useIdlePrefetch([loadGetStarted, loadFooter]);

  return (
    <>
      <SEOMetadata
        title="Comparison Articles"
        description="Compare AxionQuant with other financial data APIs. Side-by-side coverage, pricing, and feature comparisons."
        canonicalUrl="https://axionquant.com/compare"
      />

      <Wrapper>

        <div className="orb">
        <ThinkingOrb  state='solving' size={64} />
        </div>
        <Container>
          <BreadcrumbWrap>
            <Breadcrumb
              crumbs={[
                { name: 'Home', path: '/' },
                { name: 'Comparisons', path: '/compare' },
              ]}
            />
          </BreadcrumbWrap>

          <Title className="serif">Comparisons</Title>
          <Subtitle>
            Side-by-side comparisons of AxionQuant against other financial data
            providers and alternatives.
          </Subtitle>

          <List>
            {entries.map(entry => (
              <ListItem key={entry.slug} to={`/compare/${entry.slug}`}>
                <ListLeft>
                  <ListTitle className="serif">{entry.title}</ListTitle>
                  {entry.description && (
                    <ListDesc>{entry.description}</ListDesc>
                  )}
                </ListLeft>
                <ListArrow size={16} />
              </ListItem>
            ))}
          </List>
        </Container>
      </Wrapper>

      <Suspense fallback={null}>
        <GetStarted />
        <Footer />
      </Suspense>
    </>
  );
}
